import { createContext, useContext } from 'react';
import { NavLink, Outlet, useParams } from 'react-router';
import type { OrganizerEvent } from '@gatecheckpro/contracts';
import { EVENT_STATES, LoadingRegion, Status, presentation } from '@gatecheckpro/design-system';
import { useResource } from '../../lib/useResource.ts';
import { FailureNotice } from '../../lib/refusal.tsx';

/**
 * The event workspace.
 *
 * One read of the whole event feeds every section, and every section can
 * refresh it. That is what lets the sub-navigation carry a count of what still
 * needs attention *there* — an organizer should be able to see from the tabs
 * where the work is, rather than clicking through five screens to find out.
 */
interface EventWorkspaceValue {
  readonly event: OrganizerEvent;
  readonly organizationId: string;
  /**
   * A function-valued property rather than a method: every section destructures
   * it off the context, and a method shorthand would carry a `this` binding
   * that destructuring silently drops.
   */
  readonly reload: () => void;
}

const EventContext = createContext<EventWorkspaceValue | null>(null);

export function useEventWorkspace(): EventWorkspaceValue {
  const value = useContext(EventContext);
  if (value === null) throw new Error('useEventWorkspace used outside the workspace');
  return value;
}

type Section = OrganizerEvent['publishReadiness']['blockers'][number]['section'];

const SECTIONS: ReadonlyArray<{ to: string; label: string; section: Section; end?: boolean }> = [
  { to: '', label: 'Overview', section: 'OVERVIEW', end: true },
  { to: 'sessions', label: 'Sessions', section: 'SESSIONS' },
  { to: 'venue', label: 'Venue', section: 'VENUE' },
  { to: 'gates', label: 'Gates & zones', section: 'GATES' },
  { to: 'tickets', label: 'Tickets & capacity', section: 'TICKETS' },
];

/**
 * Sections with no publication blockers of their own.
 *
 * Operational access is not a precondition for publishing an event — a show
 * can go on sale before anybody has decided who is working it — so it sits
 * outside the readiness list rather than being given a blocker category it
 * would never use.
 */
const ADDITIONAL_SECTIONS: ReadonlyArray<{ to: string; label: string }> = [
  { to: 'operational-access', label: 'Operational access' },
];

export function EventWorkspace() {
  const { organizationId, eventId } = useParams<{ organizationId: string; eventId: string }>();
  const { resource, reload } = useResource<OrganizerEvent>(
    organizationId === undefined || eventId === undefined
      ? null
      : `/api/v1/organizer/organizations/${organizationId}/events/${eventId}`,
  );

  if (resource.status === 'loading') return <LoadingRegion label="Loading event" />;
  if (resource.status === 'failed') return <FailureNotice error={resource.error} />;

  const event = resource.data;
  const state = presentation(EVENT_STATES, event.status);

  const outstandingIn = (section: Section): number =>
    event.publishReadiness.blockers.filter((blocker) => blocker.section === section).length;

  return (
    <EventContext.Provider value={{ event, organizationId: organizationId as string, reload }}>
      <div className="gc-stack gc-stack--loose">
        <div className="gc-stack gc-stack--tight">
          <div className="gc-row gc-row--baseline">
            <h2>{event.title}</h2>
            <Status
              tone={state.tone}
              glyph={state.glyph}
              label={state.label}
              title={state.meaning}
            />
          </div>
          <p className="gc-muted">
            {event.venue?.name ?? 'No venue yet'} · {event.timezone} · /e/{event.slug}
          </p>
        </div>

        <nav className="gc-subnav" aria-label="Event configuration">
          {SECTIONS.map((section) => {
            const outstanding = outstandingIn(section.section);
            return (
              <NavLink
                key={section.label}
                to={section.to}
                end={section.end === true}
                className="gc-subnav__link"
              >
                {section.label}
                {outstanding > 0 && (
                  <span
                    className="gc-subnav__badge"
                    title={`${String(outstanding)} thing${outstanding === 1 ? '' : 's'} to finish before publishing`}
                  >
                    {outstanding}
                  </span>
                )}
              </NavLink>
            );
          })}
          {ADDITIONAL_SECTIONS.map((section) => (
            <NavLink key={section.label} to={section.to} className="gc-subnav__link">
              {section.label}
            </NavLink>
          ))}
        </nav>

        <Outlet />
      </div>
    </EventContext.Provider>
  );
}
